import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import DatePicker from "react-datepicker";
import OverridePopup from '../Components/OverridePopup';
import ReservationExtrasModal from "../Components/ReservationExtrasModal";
import { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import es from "date-fns/locale/es";
import ca from "date-fns/locale/ca";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { getReservationModificationBlock } from "./reservationModificationPolicy";
import {
  createReservationTranslator,
  translateReservationModificationBlock,
  translateReservationStatus,
} from "./reservationI18n";
import reservationDraftMessages from "./reservationDraftMessages";
import RoomUnassignOption from "./RoomUnassignOption";
registerLocale("en-GB", enGB);
registerLocale("es", es);
registerLocale("ca", ca);

const DATE_PICKER_LOCALE_BY_LANGUAGE = {
  en: "en-GB",
  es: "es",
  ca: "ca",
};

const toHHMM = (v) => {
  if (!v) return "";
  const s = String(v);
  // "15:00:00" -> "15:00"
  return s.length >= 5 ? s.slice(0, 5) : s;
};

const toDbTime = (v) => {
  if (!v) return null;
  const s = String(v);
  // "15:00" -> "15:00:00"
  return /^\d{2}:\d{2}$/.test(s) ? `${s}:00` : s;
};

const toLocalYYYYMMDD = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDateOnlyLocal = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d); // local, sin UTC shift
};


const getExtrasNames = (draft, t) => {
  const list =
    Array.isArray(draft?.extras_details) && draft.extras_details.length
      ? draft.extras_details
      : Array.isArray(draft?.extras) && draft.extras.length
        ? draft.extras
        : [];

  const names = list
    .map((x) => x?.name ?? x?.extra?.name)
    .filter(Boolean);

  return names.length ? names.join(", ") : t("No extras");
};

const getTotalExtras = (draft) => {
  const v = draft?.total_extras ?? draft?.totals?.total_extras ?? 0;
  return Number(v || 0);
};


//10.01.2026 - para que nos de el motivo por el que no nos deja seleccionar room rates

const formatNoRatesReason = (data, t, languageId) => {
  if (!data) return t("No room rates available.");

  // soporta varias formas típicas de backend
  const originalReason =
    data.reason ||
    data.reason_message ||
    data.message ||
    data.error ||
    null;

  const code = data.reason_code || data.code || null;
  // Backend diagnostics remain in the console; do not expose untranslated prose.
  const reason = languageId === "es" && originalReason
    ? (t(originalReason) !== originalReason
        ? t(originalReason)
        : t("No rates match the selected dates, occupancy and room category."))
    : originalReason;

  if (code && reason) return t("No valid room rates ({code}): {reason}", { code, reason });
  if (reason) return t("No valid room rates: {reason}", { reason });
  if (code) return t("No valid room rates ({code}).", { code });

  // fallback útil si mandas "skip" o contadores
  if (data.skip) return languageId === "es"
    ? t("No rates match the selected dates, occupancy and room category.")
    : t("No valid room rates. Details: {details}", { details: JSON.stringify(data.skip) });

  return t("No room rates available.");
};

const firstRoomMetaValue = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "-";
};

const getRoomMetadataLines = (room) => [
  {
    label: "Floor",
    value: firstRoomMetaValue(room?.floor, room?.room_floor),
  },
  {
    label: "Bed type",
    value: firstRoomMetaValue(room?.bed_type, room?.bedType, room?.room_bed_type),
  },
  {
    label: "View",
    value: firstRoomMetaValue(room?.view, room?.views, room?.room_view),
  },
];


/**
 * Reservation Draft Popup
 * Modal real (bootstrap-style) con backdrop correcto
 * Contiene el CLON VISUAL del ReservationForm (tarjeta izquierda)
 */
const ReservationDraftPopup = ({
  isOpen,
  reservationDraftId,
  onClose,
  onSaved,
  onPricingChanged,
  reservationModificationPolicy,
}) => {
  const { languageId, themeId } = usePmsTheme();
  const t = useMemo(
    () => createReservationTranslator(languageId, reservationDraftMessages),
    [languageId]
  );
  const isLightTheme = themeId === "light";
  const { baseCurrency, displayCurrency, formatFromBase } = useCurrencyDisplay();
  const datePickerLocale = DATE_PICKER_LOCALE_BY_LANGUAGE[languageId] || "en-GB";

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditingAdults, setIsEditingAdults] = useState(false);
  const [availableAdults, setAvailableAdults] = useState([]);
  const [isEditingChildren, setIsEditingChildren] = useState(false);
  const [availableChildren, setAvailableChildren] = useState([]);
  const [isEditingRoomCategory, setIsEditingRoomCategory] = useState(false);
  const [availableRoomCategories, setAvailableRoomCategories] = useState([]);
  const [isEditingRoom, setIsEditingRoom] = useState(false);
  const [roomSaving, setRoomSaving] = useState(false);
  const [roomSelectionError, setRoomSelectionError] = useState("");
  const roomSavingRef = useRef(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [roomRates, setRoomRates] = useState([]);
  const [extrasModalMode, setExtrasModalMode] = useState(null); // "details" | "manage" | null


  const [isOverridePopupOpen, setIsOverridePopupOpen] = useState(false);

  const refetchDraftFromDB = async () => {
    const { data } = await axios.get(`/api/reservation-drafts/${reservationDraftId}`);
    setFormData(data);
  };

  const sortRoomsByNumber = (rooms = []) =>
    [...rooms].sort((a, b) => {
      const numA = parseInt(a?.room_number, 10);
      const numB = parseInt(b?.room_number, 10);
      return Number.isNaN(numA) || Number.isNaN(numB) ? 0 : numA - numB;
    });

  const applySuggestedRoom = async (room) => {
    if (roomSavingRef.current) return;
    roomSavingRef.current = true;
    setRoomSaving(true);
    setRoomSelectionError("");
    try {
      await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
        room_id: room?.id ?? null,
        updated_by: "SelectRoom",
      });

      const { data: freshDraft } = await axios.get(`/api/reservation-drafts/${reservationDraftId}`);
      await onPricingChanged?.();
      setFormData(freshDraft);
      setIsEditingRoom(false);
    } catch (err) {
      console.error("[Room][ERROR_APPLY]", err);
      setRoomSelectionError("Error saving room.");
    } finally {
      roomSavingRef.current = false;
      setRoomSaving(false);
    }
  };



  const [isEditingDates, setIsEditingDates] = useState(false);
    const [tempRange, setTempRange] = useState({
    startDate: null,
    endDate: null,
    });


  useEffect(() => {
    if (!isOpen || !reservationDraftId) return;

    let cancelled = false;
    setLoading(true);
    setFormData(null);

    async function loadDraft() {
      try {
        const { data } = await axios.get(
          `/api/reservation-drafts/${reservationDraftId}`
        );
        if (!cancelled) setFormData(data);
      } catch (err) {
        console.error("[ReservationDraftPopup][LOAD][ERR]", err);
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDraft();
    return () => {
      cancelled = true;
    };
  }, [isOpen, reservationDraftId, onClose]);

  // 🔒 CONDICIÓN ÚNICA → sin modal NO hay backdrop
  if (!isOpen || loading || !formData) return null;

  const getDraftModificationBlock = (actions) =>
    formData.original_reservation_id
      ? translateReservationModificationBlock(getReservationModificationBlock({
          reservation: formData,
          policy: reservationModificationPolicy,
          actions,
        }), languageId)
      : null;
  const dateModificationBlock = getDraftModificationBlock([
    "dates",
    "rate_prices",
  ]);
  const guestModificationBlock = getDraftModificationBlock([
    "guests",
    "rate_prices",
  ]);
  const categoryModificationBlock = getDraftModificationBlock([
    "room_category",
    "rate_prices",
  ]);
  const ratePriceModificationBlock = getDraftModificationBlock([
    "rate_prices",
  ]);
  const showModificationBlock = (block) => {
    if (!block) return false;
    alert(block.message);
    return true;
  };
  const modificationBlockMessages = [
    dateModificationBlock?.message,
    guestModificationBlock?.message,
    categoryModificationBlock?.message,
    ratePriceModificationBlock?.message,
  ].filter((message, index, items) => message && items.indexOf(message) === index);
  const hasCompleteDateRange = Boolean(
    tempRange.startDate && tempRange.endDate && tempRange.endDate > tempRange.startDate
  );
  const selectedDateNights = hasCompleteDateRange
    ? Math.ceil((tempRange.endDate - tempRange.startDate) / (1000 * 60 * 60 * 24))
    : null;

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 1050,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
  };

  const panelStyle = {
    width: "min(1100px, 100%)",
    maxWidth: "1100px",
    background:
      "linear-gradient(145deg, rgba(8, 8, 18, 0.95), rgba(14, 14, 32, 0.98))",
    borderRadius: "28px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    padding: "26px 28px",
    boxShadow: "0 30px 80px rgba(0, 0, 0, 0.65)",
  };

  const sectionStyle = {
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.09)",
    background:
      "linear-gradient(180deg, rgba(18, 18, 40, 0.92), rgba(12, 12, 22, 0.98))",
    padding: "18px",
    marginBottom: "18px",
    boxShadow: "0 22px 45px rgba(0, 0, 0, 0.35)",
  };

  const calendarOverlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 2000,
    backdropFilter: "blur(8px)",
    backgroundColor: "rgba(2, 6, 23, 0.76)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
    padding: "10px 14px",
  };

  const calendarPanelStyle = {
    width: "min(760px, calc(100vw - 24px))",
  };

  const popupCss = `
    .rdp-shell .modal-content {
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      padding: 0 !important;
    }

    .rdp-shell .rdp-formCard {
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
        linear-gradient(135deg, rgba(61, 182, 233, 0.55), rgba(99, 193, 199, 0.65), rgba(34, 58, 122, 0.45)) border-box;
      box-shadow: 0 24px 70px rgba(0, 0, 0, 0.58);
    }

    .rdp-shell .rdp-topGrid {
      display: grid;
      grid-template-columns: minmax(320px, 1fr) minmax(420px, 1.3fr);
      gap: 12px;
      align-items: start;
    }

    @media (max-width: 1200px) {
      .rdp-shell .rdp-topGrid {
        grid-template-columns: 1fr;
      }
    }

    .rdp-shell .rdp-section {
      margin-bottom: 12px !important;
      padding: 14px !important;
      border-radius: 16px !important;
    }

    .rdp-shell .rdp-sectionTitle {
      font-size: 0.74rem;
      font-weight: 700;
      letter-spacing: 0.85px;
      text-transform: uppercase;
      color: rgba(226, 235, 255, 0.7);
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      padding-bottom: 7px;
      margin-bottom: 10px;
    }

    .rdp-shell .row.g-3 {
      --bs-gutter-y: 0.55rem;
      --bs-gutter-x: 0.75rem;
    }

    .rdp-shell .form-label {
      color: rgba(225, 232, 250, 0.78);
      font-size: 0.72rem;
      letter-spacing: 0.25px;
      margin-bottom: 4px;
      font-weight: 600;
    }

    .rdp-shell .form-control,
    .rdp-shell .form-control-sm,
    .rdp-shell .bg-light,
    .rdp-shell .bg-secondary {
      background: rgba(20, 20, 44, 0.9) !important;
      color: rgba(236, 240, 255, 0.96) !important;
      border: 1px solid rgba(255, 255, 255, 0.16) !important;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
    }

    .rdp-shell .form-control::placeholder {
      color: rgba(214, 221, 245, 0.5);
    }

    .rdp-shell .form-control:focus {
      border-color: rgba(61, 182, 233, 0.75) !important;
      box-shadow:
        0 0 0 0.2rem rgba(61, 182, 233, 0.18),
        inset 0 1px 0 rgba(255, 255, 255, 0.05) !important;
    }

    .rdp-shell .btn {
      border-radius: 12px;
      font-weight: 700;
      letter-spacing: 0.03em;
      transition:
        transform 140ms ease,
        box-shadow 140ms ease,
        border-color 140ms ease,
        background 140ms ease,
        color 140ms ease;
    }

    .rdp-shell .btn:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    .rdp-shell .btn-success {
      border: 1px solid rgba(61, 182, 233, 0.8) !important;
      color: #eff6ff !important;
      background: #223a7a !important;
      box-shadow: 0 14px 30px rgba(34, 58, 122, 0.28);
    }

    .rdp-shell .btn-outline-danger {
      color: #fecaca !important;
      border-color: rgba(248, 113, 113, 0.72) !important;
      background: rgba(127, 29, 29, 0.2) !important;
    }

    .rdp-shell .btn-outline-primary {
      color: #bae6fd !important;
      border-color: rgba(61, 182, 233, 0.58) !important;
      background: rgba(61, 182, 233, 0.14) !important;
    }

    .rdp-shell .btn-outline-success {
      color: #bdebea !important;
      border-color: rgba(99, 193, 199, 0.45) !important;
      background: rgba(99, 193, 199, 0.16) !important;
    }

    .rdp-shell .btn-outline-light {
      border-color: rgba(148, 163, 184, 0.42) !important;
      color: #e2e8f0 !important;
      background: rgba(15, 23, 42, 0.68) !important;
    }

    .rdp-shell .btn:disabled {
      opacity: 0.52;
      box-shadow: none !important;
      transform: none !important;
    }

    .rdp-pickerOverlay {
      z-index: 1120 !important;
      backdrop-filter: blur(8px);
      background: rgba(2, 6, 23, 0.76) !important;
      padding: 20px;
    }

    .rdp-pickerCard {
      width: min(560px, calc(100vw - 30px));
      max-height: min(80vh, 760px);
      overflow-y: auto;
      border-radius: 22px !important;
      border: 1px solid rgba(148, 163, 184, 0.26);
      background:
        linear-gradient(180deg, rgba(8, 15, 30, 0.97), rgba(15, 23, 42, 0.95)) padding-box,
        linear-gradient(170deg, rgba(61, 182, 233, 0.35), rgba(34, 58, 122, 0.18)) border-box !important;
      box-shadow: 0 30px 70px rgba(2, 6, 23, 0.72) !important;
      padding: 22px !important;
      color: #e2e8f0;
    }

    .rdp-pickerCard--compact {
      width: min(360px, calc(100vw - 30px));
    }

    .rdp-pickerCard--wide {
      width: min(680px, calc(100vw - 30px));
    }

    .rdp-pickerHeader {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 14px;
      margin-bottom: 18px;
    }

    .rdp-pickerHeaderCopy {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    .rdp-pickerEyebrow {
      margin: 0;
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(186, 230, 253, 0.82);
    }

    .rdp-pickerTitle {
      margin: 0;
      font-size: 1.08rem;
      font-weight: 800;
      color: #f8fafc;
    }

    .rdp-pickerSubtitle {
      margin: 0;
      font-size: 0.82rem;
      line-height: 1.45;
      color: rgba(226, 232, 240, 0.68);
    }

    .rdp-pickerClose {
      width: 40px;
      height: 40px;
      flex: 0 0 auto;
      border-radius: 12px;
      border: 1px solid rgba(148, 163, 184, 0.32);
      background: rgba(15, 23, 42, 0.7);
      color: #e2e8f0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;
      transition:
        transform 140ms ease,
        border-color 140ms ease,
        background 140ms ease;
    }

    .rdp-pickerClose:hover {
      transform: translateY(-1px);
      border-color: rgba(61, 182, 233, 0.48);
      background: rgba(30, 41, 59, 0.94);
    }

    .rdp-pickerEmpty {
      border-radius: 16px;
      border: 1px dashed rgba(148, 163, 184, 0.3);
      background: rgba(15, 23, 42, 0.6);
      color: rgba(226, 232, 240, 0.68);
      padding: 18px;
      text-align: center;
      font-size: 0.9rem;
    }

    .rdp-pickerOptions {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .rdp-pickerOption {
      border-radius: 16px;
      border: 1px solid rgba(148, 163, 184, 0.24);
      background: linear-gradient(180deg, rgba(15, 23, 42, 0.88), rgba(2, 6, 23, 0.84));
      color: #e2e8f0;
      padding: 14px 16px;
      cursor: pointer;
      transition:
        transform 140ms ease,
        border-color 140ms ease,
        box-shadow 140ms ease,
        background 140ms ease;
    }

    .rdp-pickerOption:hover {
      transform: translateY(-1px);
      border-color: rgba(61, 182, 233, 0.52);
      background: linear-gradient(180deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.9));
      box-shadow: 0 18px 26px rgba(2, 6, 23, 0.32);
    }

    .rdp-pickerOption--numeric {
      padding: 16px;
      text-align: center;
      font-size: 1rem;
      font-weight: 800;
      letter-spacing: 0.02em;
    }

    .rdp-pickerOption--occupied {
      border-color: rgba(235, 102, 121, 0.3);
      background: linear-gradient(180deg, rgba(155, 52, 69, 0.24), rgba(15, 23, 42, 0.86));
    }

    .rdp-pickerOption--occupied:hover {
      border-color: rgba(235, 102, 121, 0.5);
      background: linear-gradient(180deg, rgba(155, 52, 69, 0.34), rgba(15, 23, 42, 0.9));
    }

    .rdp-pickerOptionTitle {
      display: block;
      font-size: 0.96rem;
      font-weight: 700;
      color: #f8fafc;
    }

    .rdp-pickerOptionMeta {
      display: block;
      margin-top: 4px;
      font-size: 0.82rem;
      color: rgba(226, 232, 240, 0.66);
    }

    .rdp-pickerRoomMeta {
      display: grid;
      gap: 2px;
      min-width: 0;
    }

    .rdp-pickerRoomMetaLine {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rdp-pickerRoomMetaLabel {
      color: rgba(248, 250, 252, 0.92);
      font-weight: 700;
    }

    .rdp-pickerGroupLabel {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      margin-bottom: 10px;
      border-radius: 999px;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    .rdp-pickerGroupLabel--available {
      border: 1px solid rgba(99, 193, 199, 0.3);
      background: rgba(99, 193, 199, 0.14);
      color: #bdebea;
    }

    .rdp-pickerGroupLabel--occupied {
      border: 1px solid rgba(235, 102, 121, 0.32);
      background: rgba(155, 52, 69, 0.16);
      color: #ffc9d1;
    }

    .rdp-pickerActions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 18px;
    }

    .rdp-actionBtn {
      border-radius: 12px;
      border: 1px solid rgba(148, 163, 184, 0.32);
      background: rgba(15, 23, 42, 0.76);
      color: #e2e8f0;
      padding: 11px 18px;
      font-size: 0.9rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      cursor: pointer;
      transition:
        transform 140ms ease,
        box-shadow 140ms ease,
        border-color 140ms ease,
        background 140ms ease,
        color 140ms ease;
    }

    .rdp-actionBtn:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    .rdp-actionBtn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    .rdp-actionBtn--primary {
      border-color: rgba(61, 182, 233, 0.8);
      background: #223a7a;
      color: #eff6ff;
      box-shadow: 0 14px 28px rgba(34, 58, 122, 0.25);
    }

    .rdp-actionBtn--secondary {
      border-color: rgba(248, 113, 113, 0.72);
      background: rgba(127, 29, 29, 0.18);
      color: #fecaca;
    }

    .rdp-actionBtn--ghost {
      border-color: rgba(148, 163, 184, 0.32);
      background: rgba(15, 23, 42, 0.58);
      color: #cbd5e1;
    }

    .rdp-dateModal {
      display: flex;
      flex-direction: column;
      gap: 14px;
      max-height: none !important;
      overflow: visible !important;
    }

    .rdp-dateModal .rdp-pickerHeader {
      margin-bottom: 2px;
    }

    .rdp-dateContent {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .rdp-dateFields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .rdp-dateFieldGroup {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .rdp-dateFieldLabel {
      margin: 0;
      color: #cbd5e1;
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0.055em;
      text-transform: uppercase;
    }

    .rdp-dateField {
      width: 100%;
      border-radius: 12px;
      border: 1px solid rgba(148, 163, 184, 0.36);
      background: rgba(15, 23, 42, 0.88);
      color: #e2e8f0;
      padding: 12px 14px;
      font-size: 0.95rem;
      font-weight: 600;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
    }

    .rdp-dateField::placeholder {
      color: #94a3b8;
      opacity: 1;
    }

    .rdp-dateField:focus {
      outline: none;
      border-color: rgba(61, 182, 233, 0.72);
      box-shadow: 0 0 0 0.2rem rgba(61, 182, 233, 0.16);
    }

    .rdp-dateCalendarWrap {
      display: flex;
      justify-content: center;
      min-width: 0;
      overflow-x: auto;
      padding: 2px 0 8px;
      scrollbar-color: rgba(61, 182, 233, 0.48) transparent;
    }

    .rdp-dateCalendarIntro {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      color: #cbd5e1;
      font-size: 0.78rem;
      line-height: 1.4;
    }

    .rdp-dateCalendarIntro strong {
      color: #f8fafc;
    }

    .rdp-dateCalendarSummary {
      flex: 0 0 auto;
      padding: 5px 9px;
      border: 1px solid rgba(99, 193, 199, 0.3);
      border-radius: 999px;
      color: #bdebea;
      background: rgba(99, 193, 199, 0.12);
      font-size: 0.7rem;
      font-weight: 800;
    }

    .rdp-dateCalendar {
      border: 1px solid rgba(148, 163, 184, 0.24) !important;
      border-radius: 18px !important;
      background: linear-gradient(180deg, rgba(8, 15, 30, 0.98), rgba(15, 23, 42, 0.96)) !important;
      padding: 14px !important;
      box-shadow: 0 24px 40px rgba(2, 6, 23, 0.35);
    }

    .rdp-dateCalendar .react-datepicker__header {
      border-bottom: 1px solid rgba(148, 163, 184, 0.18) !important;
      background: transparent !important;
    }

    .rdp-dateCalendar .react-datepicker__current-month,
    .rdp-dateCalendar .react-datepicker__day-name,
    .rdp-dateCalendar .react-datepicker__day,
    .rdp-dateCalendar .react-datepicker__time-name,
    .rdp-dateCalendar .react-datepicker__month-text,
    .rdp-dateCalendar .react-datepicker__quarter-text,
    .rdp-dateCalendar .react-datepicker__year-text {
      color: #e2e8f0;
    }

    .rdp-dateCalendar .react-datepicker__day-name {
      color: rgba(186, 230, 253, 0.82);
      font-weight: 700;
    }

    .rdp-dateCalendar .react-datepicker__day,
    .rdp-dateCalendar .react-datepicker__month-text,
    .rdp-dateCalendar .react-datepicker__quarter-text,
    .rdp-dateCalendar .react-datepicker__year-text {
      border-radius: 10px;
      transition: background 140ms ease, color 140ms ease;
    }

    .rdp-dateCalendar .react-datepicker__day:hover,
    .rdp-dateCalendar .react-datepicker__month-text:hover,
    .rdp-dateCalendar .react-datepicker__quarter-text:hover,
    .rdp-dateCalendar .react-datepicker__year-text:hover {
      background: rgba(61, 182, 233, 0.16);
    }

    .rdp-dateCalendar .react-datepicker__day--selected,
    .rdp-dateCalendar .react-datepicker__day--in-range,
    .rdp-dateCalendar .react-datepicker__month-text--selected,
    .rdp-dateCalendar .react-datepicker__quarter-text--selected,
    .rdp-dateCalendar .react-datepicker__year-text--selected {
      background: #223a7a;
      color: #eff6ff !important;
    }

    .rdp-dateCalendar .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--selected) {
      color: #b9e8f5 !important;
      background: rgba(61, 182, 233, 0.12) !important;
    }

    .rdp-dateCalendar .react-datepicker__day--outside-month,
    .rdp-dateCalendar .react-datepicker__day--disabled {
      color: rgba(148, 163, 184, 0.4);
      background: transparent !important;
    }

    .rdp-dateCalendar .react-datepicker__navigation-icon::before {
      border-color: #bae6fd;
    }

    .rdp-dateCalendarDay {
      border-radius: 10px;
    }

    /* Pilcore reservation editor — light is the primary, high-legibility theme. */
    .rdp-mainOverlay--light {
      background: rgba(23, 38, 83, 0.34) !important;
      backdrop-filter: blur(7px) !important;
      padding: 14px !important;
      color-scheme: light;
    }

    .rdp-shell--light {
      width: min(1180px, calc(100vw - 28px)) !important;
      max-width: 1180px !important;
      max-height: calc(100dvh - 28px) !important;
      overflow-y: auto !important;
      overscroll-behavior: contain;
      padding: 15px 17px 13px !important;
      color: #26354f !important;
      background: #ffffff !important;
      border: 1px solid #d7e2ec !important;
      border-top: 4px solid #3db6e9 !important;
      border-radius: 13px !important;
      box-shadow: 0 24px 58px rgba(34, 58, 122, 0.22) !important;
      scrollbar-color: #9ab8cb transparent;
    }

    .rdp-shell--light .modal-content {
      color: #26354f !important;
      background: transparent !important;
    }

    .rdp-shell .rdp-mainHeader {
      min-height: 40px;
      margin-bottom: 11px !important;
      padding: 0 2px;
    }

    .rdp-shell .rdp-mainTitle {
      color: #172653 !important;
      font-size: 1.08rem;
      line-height: 1.2;
      letter-spacing: -0.015em;
    }

    .rdp-shell--light .rdp-pickerClose {
      width: 36px;
      height: 36px;
      color: #223a7a !important;
      background: #ffffff !important;
      border-color: #bdcad8 !important;
      border-radius: 9px;
    }

    .rdp-shell--light .rdp-pickerClose:hover {
      color: #172653 !important;
      background: #edf8fc !important;
      border-color: #3db6e9 !important;
    }

    .rdp-shell--light .rdp-formCard {
      zoom: 1 !important;
      padding: 0 !important;
      color: #26354f !important;
      background: #f4f8fb !important;
      border: 0 !important;
      border-radius: 10px !important;
      box-shadow: none !important;
    }

    .rdp-mainOverlay.rdp-mainOverlay--light
      .rdp-shell.rdp-shell--light
      .rdp-formCard {
      background: #f4f8fb !important;
    }

    .rdp-shell .rdp-topGrid {
      grid-template-columns: minmax(330px, 0.88fr) minmax(440px, 1.12fr);
      gap: 10px;
      align-items: stretch;
    }

    .rdp-shell--light .rdp-section {
      margin-bottom: 10px !important;
      padding: 12px 13px 11px !important;
      color: #26354f !important;
      background: #ffffff !important;
      border: 1px solid #d7e2ec !important;
      border-radius: 10px !important;
      box-shadow: 0 3px 10px rgba(34, 58, 122, 0.045) !important;
    }

    .rdp-shell .rdp-topGrid > .rdp-section {
      height: 100%;
    }

    .rdp-shell--light .rdp-sectionTitle {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 9px;
      padding-bottom: 7px;
      color: #223a7a !important;
      border-bottom: 1px solid #dce8f0;
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.075em;
    }

    .rdp-shell--light .rdp-sectionTitle::before {
      width: 3px;
      height: 11px;
      flex: 0 0 auto;
      border-radius: 999px;
      background: #3db6e9;
      content: "";
    }

    .rdp-shell .rdp-section--dates > .row,
    .rdp-shell .rdp-section--occupancy > .row {
      display: grid;
      align-items: end;
      gap: 9px;
      margin: 0 !important;
    }

    .rdp-shell .rdp-section--dates > .row {
      grid-template-columns: minmax(205px, 2fr) minmax(78px, 0.72fr);
    }

    .rdp-shell .rdp-section--occupancy > .row {
      grid-template-columns: repeat(3, minmax(105px, 1fr));
    }

    .rdp-shell .rdp-section--dates > .row > [class*="col-"],
    .rdp-shell .rdp-section--occupancy > .row > [class*="col-"] {
      width: 100%;
      max-width: none;
      margin: 0 !important;
      padding: 0 !important;
    }

    .rdp-shell .row.g-3 {
      --bs-gutter-x: 0.65rem;
      --bs-gutter-y: 0.42rem;
    }

    .rdp-shell .rdp-section .mb-3 {
      margin-bottom: 7px !important;
    }

    .rdp-shell--light .form-label {
      margin-bottom: 4px;
      color: #172653 !important;
      font-size: 0.69rem;
      font-weight: 730;
      letter-spacing: 0.005em;
    }

    .rdp-shell--light .form-control,
    .rdp-shell--light .form-control-sm,
    .rdp-shell--light .bg-light,
    .rdp-shell--light .bg-secondary {
      min-height: 38px !important;
      padding: 7px 9px !important;
      color: #26354f !important;
      background: #ffffff !important;
      border: 1px solid #bdcad8 !important;
      border-radius: 7px !important;
      box-shadow: none !important;
      font-size: 0.75rem !important;
      line-height: 1.22 !important;
      -webkit-text-fill-color: currentColor;
    }

    .rdp-shell--light .bg-light.text-dark {
      color: #26354f !important;
      background: #f8fbfd !important;
    }

    .rdp-shell--light .bg-light.text-dark:hover:not([style*="not-allowed"]) {
      background: #edf8fc !important;
      border-color: #3db6e9 !important;
    }

    .rdp-shell--light .form-control:focus,
    .rdp-shell--light .form-control-sm:focus {
      color: #26354f !important;
      background: #ffffff !important;
      border-color: #3db6e9 !important;
      outline: none !important;
      box-shadow: 0 0 0 3px rgba(61, 182, 233, 0.2) !important;
    }

    .rdp-shell--light .form-control[readonly],
    .rdp-shell--light .bg-secondary {
      color: #33415c !important;
      background: #eef3f7 !important;
      border-color: #d2dde7 !important;
      opacity: 1 !important;
    }

    .rdp-mainOverlay--light
      .rdp-shell--light
      .form-control.rdp-readonlyField {
      color: #33415c !important;
      background: #eef3f7 !important;
      border-color: #d2dde7 !important;
      opacity: 1 !important;
    }

    .rdp-mainOverlay--light .rdp-shell--light .rdp-valueField {
      color: #172653 !important;
      background: #edf8fc !important;
      border-color: #add7e5 !important;
      font-variant-numeric: tabular-nums;
    }

    .rdp-mainOverlay--light .rdp-shell--light .rdp-valueField > span {
      color: #172653 !important;
    }

    .rdp-mainOverlay--light .rdp-shell--light .rdp-valueField--total {
      box-shadow: inset 3px 0 0 #3db6e9 !important;
      font-weight: 750;
    }

    .rdp-mainOverlay--light .rdp-shell--light input.rdp-statusField {
      color: #22666a !important;
      background: #edf9f8 !important;
      border-color: #a8dcda !important;
      opacity: 1 !important;
    }

    .rdp-shell--light .form-control[style*="opacity: 0.6"],
    .rdp-shell--light .form-control[style*="opacity: 0.58"],
    .rdp-shell--light .bg-light[style*="opacity: 0.6"],
    .rdp-shell--light .bg-light[style*="opacity: 0.58"] {
      color: #68758a !important;
      background: #f1f5f8 !important;
      border-color: #d5dfe7 !important;
      opacity: 1 !important;
    }

    .rdp-shell--light .form-control::placeholder {
      color: #758196 !important;
      opacity: 1;
    }

    .rdp-shell--light .btn {
      min-height: 39px;
      border-radius: 8px;
      font-size: 0.7rem;
      letter-spacing: 0.005em;
      box-shadow: none !important;
    }

    .rdp-shell--light .btn-success {
      color: #ffffff !important;
      background: #223a7a !important;
      border-color: #223a7a !important;
      box-shadow: 0 7px 15px rgba(34, 58, 122, 0.17) !important;
    }

    .rdp-shell--light .btn-success:hover:not(:disabled) {
      background: #172653 !important;
      border-color: #172653 !important;
    }

    .rdp-shell--light .btn-outline-primary {
      color: #223a7a !important;
      background: #edf8fc !important;
      border-color: #add7e5 !important;
    }

    .rdp-shell--light .btn-outline-success {
      color: #22666a !important;
      background: #edf9f8 !important;
      border-color: #a8dcda !important;
    }

    .rdp-shell--light .btn-outline-danger {
      color: #9b3445 !important;
      background: #fdeff1 !important;
      border-color: #f2cdd3 !important;
    }

    .rdp-shell--light .btn-outline-danger:hover:not(:disabled) {
      color: #ffffff !important;
      background: #9b3445 !important;
      border-color: #9b3445 !important;
    }

    .rdp-shell--light .btn:disabled {
      color: #637187 !important;
      background: #eef2f5 !important;
      border-color: #d3dee6 !important;
      box-shadow: none !important;
      opacity: 1 !important;
      -webkit-text-fill-color: #637187 !important;
    }

    .rdp-mainOverlay--light .rdp-shell--light .btn:disabled {
      color: #5b6680 !important;
      background: #edf2f6 !important;
      border-color: #d2dde7 !important;
      opacity: 1 !important;
      -webkit-text-fill-color: #5b6680 !important;
    }

    .rdp-shell .rdp-formActions {
      position: sticky;
      bottom: -1px;
      z-index: 6;
      margin-top: 0 !important;
      padding: 9px 0 2px;
    }

    .rdp-shell--light .rdp-formActions {
      background: linear-gradient(180deg, rgba(244, 248, 251, 0.88), #f4f8fb 32%);
      border-top: 1px solid #d7e2ec;
    }

    .rdp-shell .rdp-formActions .btn {
      height: 41px !important;
      min-height: 41px !important;
    }

    .rdp-shell--light .rdp-protectedNotice {
      color: #26354f !important;
      background: #edf8fc !important;
      border-color: #add7e5 !important;
      border-radius: 9px !important;
      box-shadow: none !important;
    }

    .rdp-shell--light .rdp-protectedNotice > div {
      color: #223a7a !important;
    }

    .rdp-shell--light .rdp-policyNotice {
      color: #7d2938 !important;
      background: #fdeff1 !important;
      border-color: #f2cdd3 !important;
      border-radius: 9px !important;
    }

    .rdp-mainOverlay--dark {
      background: rgba(5, 10, 27, 0.78) !important;
      backdrop-filter: blur(8px) !important;
      padding: 14px !important;
      color-scheme: dark;
    }

    .rdp-shell--dark {
      width: min(1180px, calc(100vw - 28px)) !important;
      max-width: 1180px !important;
      max-height: calc(100dvh - 28px) !important;
      overflow-y: auto !important;
      padding: 15px 17px 13px !important;
      color: #edf4ff !important;
      background: #0b1430 !important;
      border: 1px solid rgba(61, 182, 233, 0.34) !important;
      border-top: 4px solid #3db6e9 !important;
      border-radius: 13px !important;
      box-shadow: 0 26px 72px rgba(0, 0, 0, 0.56) !important;
    }

    .rdp-shell--dark .rdp-formCard {
      zoom: 1 !important;
      padding: 0 !important;
      color: #edf4ff !important;
      background: #0d1730 !important;
      border: 0 !important;
      border-radius: 10px !important;
      box-shadow: none !important;
    }

    .rdp-shell--dark .rdp-mainTitle,
    .rdp-shell--dark .rdp-sectionTitle,
    .rdp-shell--dark .form-label {
      color: #f4f8ff !important;
    }

    .rdp-shell--dark .rdp-section {
      margin-bottom: 10px !important;
      padding: 12px 13px 11px !important;
      background: #101a36 !important;
      border: 1px solid rgba(99, 193, 199, 0.2) !important;
      border-radius: 10px !important;
      box-shadow: none !important;
    }

    .rdp-shell--dark .rdp-sectionTitle {
      margin-bottom: 9px;
      padding-bottom: 7px;
      border-bottom-color: rgba(99, 193, 199, 0.2);
      font-size: 0.68rem;
      font-weight: 800;
      letter-spacing: 0.075em;
    }

    .rdp-shell--dark .form-control,
    .rdp-shell--dark .form-control-sm,
    .rdp-shell--dark .bg-light,
    .rdp-shell--dark .bg-secondary {
      min-height: 38px !important;
      padding: 7px 9px !important;
      color: #edf4ff !important;
      background: #172653 !important;
      border: 1px solid rgba(99, 193, 199, 0.3) !important;
      border-radius: 7px !important;
      box-shadow: none !important;
      font-size: 0.75rem !important;
      line-height: 1.22 !important;
      -webkit-text-fill-color: currentColor;
    }

    .rdp-shell--dark .form-control[readonly],
    .rdp-shell--dark .bg-secondary {
      color: #dce7f7 !important;
      background: #121f42 !important;
    }

    .rdp-mainOverlay--dark .rdp-shell--dark .rdp-valueField,
    .rdp-mainOverlay--dark .rdp-shell--dark .rdp-valueField > span {
      color: #edf4ff !important;
    }

    .rdp-mainOverlay--dark .rdp-shell--dark .rdp-valueField {
      background: #172653 !important;
      border-color: rgba(61, 182, 233, 0.34) !important;
    }

    .rdp-mainOverlay--dark .rdp-shell--dark input.rdp-statusField {
      color: #bdebea !important;
      background: rgba(99, 193, 199, 0.13) !important;
      border-color: rgba(99, 193, 199, 0.38) !important;
    }

    .rdp-shell--dark .btn:disabled {
      color: #a9b7cc !important;
      background: #182440 !important;
      border-color: rgba(99, 193, 199, 0.2) !important;
      opacity: 1 !important;
      -webkit-text-fill-color: #a9b7cc !important;
    }

    .rdp-shell--dark .btn-success {
      color: #ffffff !important;
      background: #223a7a !important;
      border-color: #3db6e9 !important;
      box-shadow: 0 8px 18px rgba(61, 182, 233, 0.14) !important;
    }

    .rdp-shell--dark .btn-outline-primary,
    .rdp-shell--dark .btn-outline-success {
      color: #bdebea !important;
      background: rgba(99, 193, 199, 0.1) !important;
      border-color: rgba(99, 193, 199, 0.38) !important;
    }

    .rdp-shell--dark .btn-outline-danger {
      color: #ffc9d1 !important;
      background: rgba(155, 52, 69, 0.18) !important;
      border-color: rgba(235, 102, 121, 0.52) !important;
    }

    .rdp-shell--dark .rdp-formActions {
      background: linear-gradient(180deg, rgba(13, 23, 48, 0.9), #0d1730 32%);
      border-top: 1px solid rgba(99, 193, 199, 0.2);
    }

    /* Pickers use the same light-first hierarchy even when they sit outside the shell. */
    html[data-pms-theme="light"] .rdp-pickerOverlay {
      background: rgba(23, 38, 83, 0.38) !important;
      backdrop-filter: blur(7px) !important;
    }

    html[data-pms-theme="light"] .rdp-pickerCard {
      color: #26354f !important;
      background: #ffffff !important;
      border: 1px solid #d7e2ec !important;
      border-top: 4px solid #3db6e9 !important;
      border-radius: 12px !important;
      box-shadow: 0 24px 58px rgba(34, 58, 122, 0.22) !important;
    }

    html[data-pms-theme="light"] .rdp-pickerTitle,
    html[data-pms-theme="light"] .rdp-pickerOptionTitle,
    html[data-pms-theme="light"] .rdp-pickerRoomMetaLabel {
      color: #172653 !important;
    }

    html[data-pms-theme="light"] .rdp-pickerEyebrow {
      color: #223a7a !important;
    }

    html[data-pms-theme="light"] .rdp-pickerSubtitle,
    html[data-pms-theme="light"] .rdp-pickerOptionMeta,
    html[data-pms-theme="light"] .rdp-pickerRoomMetaLine,
    html[data-pms-theme="light"] .rdp-pickerEmpty {
      color: #5b6680 !important;
    }

    html[data-pms-theme="light"] .rdp-pickerOption,
    html[data-pms-theme="light"] .rdp-pickerEmpty {
      color: #26354f !important;
      background: #f7fafc !important;
      border-color: #d7e2ec !important;
    }

    html[data-pms-theme="light"] .rdp-pickerOption:hover,
    html[data-pms-theme="light"] .rdp-pickerOption:focus-visible {
      background: #edf8fc !important;
      border-color: #3db6e9 !important;
      box-shadow: 0 8px 18px rgba(34, 58, 122, 0.1) !important;
    }

    html[data-pms-theme="light"] .rdp-pickerOption--occupied {
      background: #fdeff1 !important;
      border-color: #f2cdd3 !important;
    }

    html[data-pms-theme="light"] .rdp-pickerGroupLabel--available {
      color: #17665b !important;
      background: #edf9f8 !important;
      border-color: #63c1c7 !important;
    }

    html[data-pms-theme="light"] .rdp-pickerGroupLabel--occupied {
      color: #9b3445 !important;
      background: #fdeff1 !important;
      border-color: #f2cdd3 !important;
    }

    html[data-pms-theme="light"] .rdp-actionBtn {
      color: #223a7a !important;
      background: #ffffff !important;
      border-color: #bdcad8 !important;
      border-radius: 8px;
    }

    html[data-pms-theme="light"] .rdp-actionBtn--primary {
      color: #ffffff !important;
      background: #223a7a !important;
      border-color: #223a7a !important;
      box-shadow: 0 8px 18px rgba(34, 58, 122, 0.17) !important;
    }

    html[data-pms-theme="light"] .rdp-actionBtn--secondary {
      color: #9b3445 !important;
      background: #ffffff !important;
      border-color: #ddaab3 !important;
    }

    html[data-pms-theme="light"] .rdp-actionBtn:disabled {
      color: #5b6680 !important;
      background: #edf2f6 !important;
      border-color: #d7e2ec !important;
      box-shadow: none !important;
      opacity: 1 !important;
      cursor: not-allowed !important;
    }

    html[data-pms-theme="light"] .rdp-dateField {
      color: #26354f !important;
      background: #ffffff !important;
      border-color: #bdcad8 !important;
      border-radius: 8px;
    }

    html[data-pms-theme="light"] .rdp-dateField::placeholder {
      color: #66738a !important;
      opacity: 1 !important;
    }

    html[data-pms-theme="light"] .rdp-dateFieldLabel {
      color: #33415c !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendarIntro {
      color: #5b6680 !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendarIntro strong {
      color: #172653 !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendarSummary {
      color: #22666a !important;
      background: #edf9f8 !important;
      border-color: #a8dcda !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar {
      color: #26354f !important;
      background: #ffffff !important;
      border-color: #d7e2ec !important;
      box-shadow: 0 12px 28px rgba(34, 58, 122, 0.13) !important;
      overflow: hidden;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__header {
      color: #172653 !important;
      background: #edf8fc !important;
      border-color: #d7e2ec !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__month-container + .react-datepicker__month-container {
      border-left: 1px solid #d7e2ec;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__current-month {
      color: #172653 !important;
      font-weight: 850 !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day-name {
      color: #465570 !important;
      font-weight: 750 !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__time-name,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__month-text,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__quarter-text,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__year-text {
      color: #26354f !important;
      background: transparent !important;
      border-radius: 8px !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day:hover {
      color: #172653 !important;
      background: #edf8fc !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--in-selecting-range,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--in-range {
      color: #172653 !important;
      background: #dff4f2 !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--selected,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--range-start,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--range-end {
      color: #ffffff !important;
      background: #223a7a !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--selected):not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end) {
      color: #172653 !important;
      background: #edf8fc !important;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--outside-month,
    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__day--disabled {
      color: #9aa6b8 !important;
      background: transparent !important;
      opacity: 0.72;
    }

    html[data-pms-theme="light"] .rdp-dateCalendar .react-datepicker__navigation-icon::before {
      border-color: #223a7a !important;
    }

    html[data-pms-theme="dark"] .rdp-pickerOption--occupied,
    html[data-pms-theme="dark"] .rdp-pickerGroupLabel--occupied {
      color: #ffc9d1 !important;
      background: rgba(155, 52, 69, 0.18) !important;
      border-color: rgba(235, 102, 121, 0.45) !important;
    }

    html[data-pms-theme="dark"] .rdp-actionBtn--primary,
    html[data-pms-theme="dark"] .rdp-dateCalendar .react-datepicker__day--selected,
    html[data-pms-theme="dark"] .rdp-dateCalendar .react-datepicker__day--in-range {
      color: #ffffff !important;
      background: #223a7a !important;
      border-color: #3db6e9 !important;
    }

    @media (max-width: 880px) {
      .rdp-shell .rdp-topGrid {
        grid-template-columns: 1fr;
      }

      .rdp-shell .rdp-section--dates > .row,
      .rdp-shell .rdp-section--occupancy > .row {
        grid-template-columns: 1fr;
      }

      .rdp-shell .rdp-formActions {
        flex-direction: column;
      }
    }

    @media (max-height: 820px) and (min-width: 881px) {
      .rdp-shell--light,
      .rdp-shell--dark {
        padding: 10px 13px 9px !important;
      }

      .rdp-shell .rdp-mainHeader {
        min-height: 34px;
        margin-bottom: 7px !important;
      }

      .rdp-shell .rdp-section {
        margin-bottom: 7px !important;
        padding: 9px 10px 8px !important;
      }

      .rdp-shell .rdp-sectionTitle {
        margin-bottom: 6px;
        padding-bottom: 5px;
      }

      .rdp-shell .rdp-section .mb-3 {
        margin-bottom: 4px !important;
      }

      .rdp-shell .form-control,
      .rdp-shell .form-control-sm,
      .rdp-shell .bg-light,
      .rdp-shell .bg-secondary {
        min-height: 35px !important;
      }

      .rdp-shell .btn {
        min-height: 36px;
      }

      .rdp-shell .rdp-formActions {
        padding-top: 6px;
      }

      .rdp-shell .rdp-formActions .btn {
        height: 37px !important;
        min-height: 37px !important;
      }
    }

    @media (max-width: 640px) {
      .rdp-pickerHeader {
        align-items: center;
      }

      .rdp-dateFields {
        grid-template-columns: 1fr;
      }

      .rdp-dateCalendarIntro {
        align-items: flex-start;
        flex-direction: column;
      }

      .rdp-dateCalendar .react-datepicker__month-container + .react-datepicker__month-container {
        display: none;
      }

      .rdp-dateModal .rdp-pickerActions {
        display: grid;
        grid-template-columns: 1fr;
      }

      .rdp-dateModal .rdp-actionBtn {
        width: 100%;
      }
    }
  `;

  return (
    <>
      <style>{popupCss}</style>
      {/* ───────── MODAL ───────── */}
      <div
        className={`rdp-mainOverlay rdp-mainOverlay--${isLightTheme ? "light" : "dark"}`}
        style={overlayStyle}
        onClick={roomSaving ? undefined : onClose}
      >
        <div
          className={`rdp-shell rdp-shell--${isLightTheme ? "light" : "dark"}`}
          style={panelStyle}
          onClick={(e) => e.stopPropagation()}
        >
            <div className="modal-content rdp-modalContent">

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3 rdp-mainHeader">
                <h4 className="fw-bold m-0 rdp-mainTitle">
                {t("Reservation")} {formData.locator}
              </h4>

              <button
                type="button"
                className="rdp-pickerClose"
                aria-label={t("Close")}
                disabled={roomSaving}
                onClick={onClose}
              >
                &times;
                </button>
              </div>

              {formData.source_block_id ? (
                <div
                  className="rdp-protectedNotice"
                  style={{
                    borderRadius: "16px",
                    border: "1px solid rgba(34, 211, 238, 0.28)",
                    background:
                      "linear-gradient(135deg, rgba(8, 47, 73, 0.88), rgba(14, 116, 144, 0.22))",
                    padding: "14px 16px",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ fontSize: "0.78rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#a5f3fc", fontWeight: 800 }}>
                    {t("Protected draft")}
                  </div>
                  <div style={{ marginTop: "6px", color: "#e0f2fe", fontWeight: 700 }}>
                    {t("This reservation was opened from an existing room block. The protected slot stays blocked until the reservation group is confirmed.")}
                  </div>
                  {formData.source_waitlist_entry ? (
                    <div style={{ marginTop: "8px", color: "#bae6fd", fontSize: "0.88rem" }}>
                      {t("Waitlist lead:")} {formData.source_waitlist_entry.guest_name || t("Guest")}
                      {formData.source_waitlist_entry.guest_email ? ` · ${formData.source_waitlist_entry.guest_email}` : ""}
                      {formData.source_waitlist_entry.guest_phone ? ` · ${formData.source_waitlist_entry.guest_phone}` : ""}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {modificationBlockMessages.length > 0 ? (
                <div
                  className="rdp-policyNotice"
                  style={{
                    borderRadius: "16px",
                    border: "1px solid rgba(235, 102, 121, 0.38)",
                    background: "rgba(155, 52, 69, 0.2)",
                    padding: "13px 15px",
                    marginBottom: "16px",
                    color: "#ffc9d1",
                  }}
                >
                  <div style={{ fontWeight: 850 }}>{t("Manual modification limits")}</div>
                  {modificationBlockMessages.map((message) => (
                    <div key={message} style={{ marginTop: "5px", fontSize: "0.82rem" }}>
                      {message}
                    </div>
                  ))}
                </div>
              ) : null}

              {/* ───── CLON TARJETA IZQUIERDA ───── */}
              <div
                className="card rdp-formCard"
              style={{
                fontSize: "0.85rem",
                width: "100%",
              }}
            >
              <div className="rdp-topGrid">
              <section className="rdp-section rdp-section--dates" style={sectionStyle}>
                <div className="rdp-sectionTitle">{t("Dates")}</div>
                <div className="row g-3 align-items-end">

                <div className="col-md-4 mb-3">
                <label className="form-label">{t("Check-In → Check-Out")}</label>

                <div
                    className="form-control form-control-sm bg-light text-dark"
                    style={{
                    cursor: dateModificationBlock ? "not-allowed" : "pointer",
                    opacity: dateModificationBlock ? 0.58 : 1,
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minHeight: "42px",
                    display: "flex",
                    alignItems: "center",
                    }}
                    title={dateModificationBlock?.message}
                    onClick={() => {
                    if (showModificationBlock(dateModificationBlock)) return;
                    setTempRange({
                      startDate: parseDateOnlyLocal(formData.check_in_date),
                      endDate: parseDateOnlyLocal(formData.check_out_date),
                    });

                    setIsEditingDates(true);
                    }}
                >
                    <span
                    style={
                        formData.check_in_date && formData.check_out_date
                        ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                        : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                    }
                    >
                    {formData.check_in_date && formData.check_out_date
                        ? `${formData.check_in_date} → ${formData.check_out_date}`
                        : t("Click to Select Dates")}
                    </span>
                </div>
                </div>


                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Nights")}</label>
                  <input
                    readOnly
                    className="form-control bg-secondary text-light fw-bold rdp-readonlyField"
                    value={formData.room_nights ?? ""}
                  />
                </div>

              </div>
              </section>
              <section className="rdp-section rdp-section--occupancy" style={sectionStyle}>
                <div className="rdp-sectionTitle">{t("Occupancy")}</div>
                <div className="row g-3 align-items-end">

                <div className="col-md-2 mb-3">
                <label className="form-label">{t("Adults")}</label>

                <div
                    className="form-control form-control-sm bg-light text-dark"
                    style={{
                    cursor: guestModificationBlock || formData.group_rate_id
                        ? "not-allowed"
                        : formData.check_in_date && formData.check_out_date
                        ? "pointer"
                        : "default",
                    opacity: guestModificationBlock || formData.group_rate_id
                        ? 0.6
                        : formData.check_in_date && formData.check_out_date
                        ? 1
                        : 0.6,
                    minHeight: "42px",
                    display: "flex",
                    alignItems: "center",
                    }}
                    title={guestModificationBlock?.message}
                    //HASTA AQUÍ ESTÁ ESTABLE

                    onClick={async () => {
                      if (showModificationBlock(guestModificationBlock)) return;
                      if (formData.group_rate_id) {
                        alert(
                          t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation.")
                        );
                        return;
                      }

                      if (!formData.check_in_date || !formData.check_out_date) return;

                      try {
                        const {
                          check_in_date,
                          check_out_date,
                          reservation_group_id,
                          business_unit_id,
                        } = formData;

                        // 1️⃣ Reset a “solo fechas” (irreversible)
                        await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                          adults: null,
                          children: null,
                          room_category_id: null,
                          room_id: null,
                          rate_id: null,
                          group_rate_id: null,
                          total_price: null,
                          night_price: null,
                          has_override: false,
                          updated_by: "AdultsOpenResetToDatesOnly",
                        });

                        // 🔥 1.1️⃣ Refetch y pintar YA el reset (BD manda)
                        const { data: afterResetDraft } = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}`
                        );
                        setFormData(afterResetDraft);

                        // 2️⃣ Rehacer snapshot desde availability (base = solo fechas)
                        await axios.post(`/api/reservation-drafts/draft-availability/populate`, {
                          reservation_draft_id: reservationDraftId,
                          reservation_group_id,
                          business_unit_id,
                          start_date: check_in_date,
                          end_date: check_out_date,
                        });

                        // 3️⃣ Rebuild flags desde snapshot fresco
                        await axios.post(`/api/reservation-drafts/${reservationDraftId}/rebuild-flags`);

                        // 4️⃣ Leer opciones YA ACTUALIZADAS desde flags
                        const res = await axios.get(`/api/reservation-drafts/${reservationDraftId}/valid-adults`);


                        setAvailableAdults(res.data.valid_adults || []);
                        setIsEditingAdults(true);

                        console.log("[Adults][LOAD_OPTIONS][AFTER_RESET+POPULATE+REBUILD]", {
                          reservation_draft_id: reservationDraftId,
                          options: res.data.valid_adults,
                        });
                      } catch (err) {
                        console.error("[Adults][ERROR_LOADING_OPTIONS]", err);
                        alert(t("Error loading adults options."));
                      }
                    }}

                >
                    <span
                    style={
                        formData.adults
                        ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                        : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                    }
                    >
                    {formData.adults ?? t("Select")}
                    </span>
                </div>

                {isEditingAdults && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
                    style={{ zIndex: 1050 }}
                  >
                    <div className="rdp-pickerCard rdp-pickerCard--compact">
                      <div className="rdp-pickerHeader">
                        <div className="rdp-pickerHeaderCopy">
                          <p className="rdp-pickerEyebrow">{t("Occupancy")}</p>
                          <h5 className="rdp-pickerTitle">{t("Select Adults")}</h5>
                          <p className="rdp-pickerSubtitle">
                            {t("Choose how many adults will stay in this reservation.")}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="rdp-pickerClose"
                          aria-label={t("Close")}
                          onClick={async () => {
                            try {
                              const { data: freshDraft } = await axios.get(
                                `/api/reservation-drafts/${reservationDraftId}`
                              );
                              setFormData(freshDraft);
                            } catch (err) {
                              console.error("[Adults][CLOSE][REFRESH_ERROR]", err);
                            } finally {
                              setIsEditingAdults(false);
                            }
                          }}
                        >
                          &times;
                        </button>
                      </div>

                      {availableAdults.length === 0 && (
                        <div className="rdp-pickerEmpty">{t("No options available")}</div>
                      )}

                      {availableAdults.length > 0 && (
                        <div className="rdp-pickerOptions">
                          {availableAdults.map((num) => (
                            <div
                              key={num}
                              className="rdp-pickerOption rdp-pickerOption--numeric"
                              onClick={async () => {
                                try {
                                  console.log("[Adults][SELECT]", {
                                    reservation_draft_id: reservationDraftId,
                                    adults: num,
                                  });

                                  // 1️⃣ Guardar adults (solo persistencia)
                                  await axios.patch(
                                    `/api/reservation-drafts/${reservationDraftId}`,
                                    {
                                      adults: num,
                                      updated_by: "SelectAdults",
                                    }
                                  );

                                  // 2️⃣ 🔥 Rebuild flags EXPLÍCITO (solo ESTE draft)
                                  await axios.post(
                                    `/api/reservation-drafts/${reservationDraftId}/rebuild-flags`
                                  );

                                  // 3️⃣ Refetch final (BD manda)
                                  const { data: freshDraft } = await axios.get(
                                    `/api/reservation-drafts/${reservationDraftId}`
                                  );

                                  setFormData(freshDraft);
                                  setIsEditingAdults(false);

                                  console.log("[Adults][SELECT][OK]", {
                                    reservation_draft_id: reservationDraftId,
                                    adults: num,
                                  });

                                } catch (err) {
                                  console.error("[Adults][SELECT][ERROR]", err);
                                  alert(t("Error saving adults."));
                                }
                              }}
                            >
                              {num}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="rdp-pickerActions">
                        <button
                          type="button"
                          className="rdp-actionBtn rdp-actionBtn--secondary"
                          onClick={() => setIsEditingAdults(false)}
                        >
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              
                {/*HASTA AQUÍ ESTÁ ESTABLE, ANTES DE TOCAR CHILDREN*/}


                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Children")}</label>

                  <div
                    className="form-control form-control-sm bg-light text-dark"
                    style={{
                      cursor: guestModificationBlock || formData.group_rate_id
                        ? "not-allowed"
                        : formData.check_in_date && formData.check_out_date
                        ? "pointer"
                        : "default",
                      opacity: guestModificationBlock || formData.group_rate_id
                        ? 0.6
                        : formData.check_in_date && formData.check_out_date
                        ? 1
                        : 0.6,
                      minHeight: "42px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title={guestModificationBlock?.message}
                    //HASTA AQUÍ ESTÁ ESTABLE PARA ADULTS. PASANDO A MODIFICAR CHILDREN.
                    onClick={async () => {
                      if (showModificationBlock(guestModificationBlock)) return;
                      if (formData.group_rate_id) {
                        alert(
                          t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation.")
                        );
                        return;
                      }

                      if (!formData.check_in_date || !formData.check_out_date) return;

                      try {
                        const {
                          check_in_date,
                          check_out_date,
                          reservation_group_id,
                          business_unit_id,
                        } = formData;

                        const resetPayload = {
                          children: null,
                          room_category_id: null,
                          room_id: null,
                          rate_id: null,
                          group_rate_id: null,
                          total_price: null,
                          night_price: null,
                          has_override: false,
                          updated_by: "ChildrenOpenResetToDatesPlusAdults",
                        };

                        console.log("[Children][OPEN][BEFORE_RESET]", {
                          reservation_draft_id: reservationDraftId,
                          formData_adults: formData.adults,
                          formData_children: formData.children,
                          resetPayload_keys: Object.keys(resetPayload),
                          resetPayload,
                        });

                        // 1️⃣ Reset a “fechas + adults” (do NOT touch adults)
                        await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, resetPayload);

                        // 1.1️⃣ Refetch and render reset immediately (DB is source of truth)
                        const { data: afterResetDraft } = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}`
                        );

                        console.log("[Children][OPEN][AFTER_RESET_GET]", {
                          reservation_draft_id: reservationDraftId,
                          afterReset_adults: afterResetDraft?.adults,
                          afterReset_children: afterResetDraft?.children,
                          afterReset_room_category_id: afterResetDraft?.room_category_id,
                          afterReset_rate_id: afterResetDraft?.rate_id,
                        });

                        setFormData(afterResetDraft);

                        // 2️⃣ Rebuild snapshot (CHILDREN SAFE: NO toca adults)
                        await axios.post(`/api/reservation-drafts/draft-availability/populate-children-reset`, {
                          reservation_draft_id: reservationDraftId,
                          reservation_group_id,
                          business_unit_id,
                          start_date: check_in_date,
                          end_date: check_out_date,
                        });

                        // 3️⃣ Rebuild flags from fresh snapshot
                        await axios.post(
                          `/api/reservation-drafts/${reservationDraftId}/rebuild-flags`
                        );


                        // 4️⃣ Read UPDATED options from flags
                        const res = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}/valid-children`
                        );

                        setAvailableChildren(res.data.valid_children || []);
                        setIsEditingChildren(true);

                        console.log("[Children][LOAD_OPTIONS][AFTER_RESET+POPULATE+REBUILD]", {
                          reservation_draft_id: reservationDraftId,
                          options: res.data.valid_children,
                        });
                      } catch (err) {
                        console.error("[Children][ERROR_LOADING_OPTIONS]", err);
                        alert(t("Error loading children options."));
                      }
                    }}

                  >
                    <span
                      style={
                        formData.children > 0
                          ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                          : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                      }
                    >
                      {formData.children ?? 0}
                    </span>
                  </div>

                  {isEditingChildren && (
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
                      style={{ zIndex: 1050 }}
                    >
                      <div className="rdp-pickerCard rdp-pickerCard--compact">
                        <div className="rdp-pickerHeader">
                          <div className="rdp-pickerHeaderCopy">
                            <p className="rdp-pickerEyebrow">{t("Occupancy")}</p>
                            <h5 className="rdp-pickerTitle">{t("Select Children")}</h5>
                            <p className="rdp-pickerSubtitle">
                              {t("Set the number of children for the selected stay.")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rdp-pickerClose"
                            aria-label={t("Close")}
                            onClick={() => setIsEditingChildren(false)}
                          >
                            &times;
                          </button>
                        </div>

                        {availableChildren.length === 0 && (
                          <div className="rdp-pickerEmpty">{t("No options available")}</div>
                        )}

                        {availableChildren.length > 0 && (
                          <div className="rdp-pickerOptions">
                            {availableChildren.map((num) => (
                              <div
                                key={num}
                                className="rdp-pickerOption rdp-pickerOption--numeric"
                                onClick={async () => {
                                  try {
                                    console.log("[Children][SELECT]", {
                                      reservation_draft_id: reservationDraftId,
                                      children: num,
                                    });

                                    // 1️⃣ Save children
                                    await axios.patch(
                                      `/api/reservation-drafts/${reservationDraftId}`,
                                      {
                                        children: num,
                                        updated_by: "SelectChildren",
                                      }
                                    );

                                    // 2️⃣ Rebuild flags explicitly (this draft only)
                                    await axios.post(
                                      `/api/reservation-drafts/${reservationDraftId}/rebuild-flags`
                                    );

                                    // 3️⃣ Refetch final (DB is source of truth)
                                    const { data: freshDraft } = await axios.get(
                                      `/api/reservation-drafts/${reservationDraftId}`
                                    );

                                    setFormData(freshDraft);
                                    setIsEditingChildren(false);

                                    console.log("[Children][SELECT][OK]", {
                                      reservation_draft_id: reservationDraftId,
                                      children: num,
                                    });

                                  } catch (err) {
                                    console.error("[Children][SELECT][ERROR]", err);
                                    alert(t("Error saving children."));
                                  }
                                }}
                              >
                                {num}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="rdp-pickerActions">
                          <button
                            type="button"
                            className="rdp-actionBtn rdp-actionBtn--secondary"
                            onClick={() => setIsEditingChildren(false)}
                          >
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>



                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Babies")}</label>

                  <input
                    key={`${reservationDraftId}-${formData.babies ?? "null"}`}   // force repaint when DB changes
                    type="number"
                    name="babies"
                    defaultValue={formData.babies ?? 0}                           // DB → UI
                    min="0"
                    disabled={
                      Boolean(guestModificationBlock) ||
                      !formData.check_in_date ||
                      !formData.check_out_date
                    }
                    title={guestModificationBlock?.message}
                    onBlur={async (e) => {
                      if (!formData.check_in_date || !formData.check_out_date) return;

                      const raw = e.target.value;
                      const babies = raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0);

                      try {
                        // 🔥 1️⃣ PATCH BD
                        await axios.patch(
                          `/api/reservation-drafts/${reservationDraftId}`,
                          {
                            babies,
                            updated_by: "SelectBabies",
                          }
                        );

                        // 🔁 2️⃣ RELEER DRAFT (BD manda)
                        const { data: freshDraft } = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}`
                        );

                        // ✅ 3️⃣ PINTAR SOLO BD
                        setFormData(freshDraft);

                        console.log("[Babies][APPLY]", {
                          reservation_draft_id: reservationDraftId,
                          babies,
                        });
                      } catch (err) {
                        console.error("[Babies][ERROR_APPLY]", err);
                        alert(t("Error saving babies."));
                      }
                    }}
                    className="form-control form-control-sm p-1 bg-light text-dark"
                    style={{
                      minHeight: "42px",
                      fontSize: (formData.babies ?? 0) > 0 ? "0.95rem" : "0.875rem",
                      fontWeight: (formData.babies ?? 0) > 0 ? "600" : "400",
                      color: (formData.babies ?? 0) > 0 ? "#212529" : "#6c757d",
                      opacity: formData.check_in_date && formData.check_out_date ? 1 : 0.6,
                    }}
                  />
                </div>

              </div>
              </section>
              </div>
              <section className="rdp-section rdp-section--rates" style={sectionStyle}>
                <div className="rdp-sectionTitle">{t("Room and Rates")}</div>
                <div className="row g-3">

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Room Category")}</label>

                  <div
                    className="form-control form-control-sm bg-light text-dark"
                    style={{
                      cursor: categoryModificationBlock || formData.group_rate_id
                        ? "not-allowed"
                        : formData.adults !== null
                        ? "pointer"
                        : "default",
                      opacity: categoryModificationBlock || formData.group_rate_id
                        ? 0.6
                        : formData.adults !== null
                        ? 1
                        : 0.6,
                      minHeight: "42px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title={categoryModificationBlock?.message}
                    //cambios en front para que room category se resetee al tocarlo y se ofrezca a si misma
                    onClick={async () => {
                      if (showModificationBlock(categoryModificationBlock)) return;
                      if (formData.group_rate_id) return;
                      if (formData.adults === null) return;

                      try {
                        // 🔥 1️⃣ Overrides fuera ANTES (backend manda)
                        if (formData.has_override) {
                          await axios.delete(
                            `/api/reservation-drafts/${reservationDraftId}/overrides`
                          );

                          const { data: refreshedDraft } = await axios.get(
                            `/api/reservation-drafts/${reservationDraftId}`
                          );

                          setFormData(refreshedDraft);
                        }

                        // ✅ 2️⃣ OPEN RESET de Room Category (libera self-consumption + resetea dependencias)
                        await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                          updated_by: "RoomCategoryOpenResetToDatesPlusOccupancy",
                        });

                        // 🔁 3️⃣ Releer draft (BD manda)
                        const { data: freshDraftAfterReset } = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}`
                        );
                        setFormData(freshDraftAfterReset);

                        // 🔎 4️⃣ Leer categorías válidas desde BD (ya sin el bloqueo “self”)
                        const res = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}/valid-room-categories`
                        );

                        setAvailableRoomCategories(res.data.categories || []);
                        setIsEditingRoomCategory(true);

                        console.log("[RoomCategory][OPEN]", {
                          reservation_draft_id: reservationDraftId,
                          adults: freshDraftAfterReset?.adults,
                          room_category_id: freshDraftAfterReset?.room_category_id,
                        });
                      } catch (err) {
                        console.error("[RoomCategory][ERROR_OPEN]", err);
                        alert(t("Error loading room categories."));
                      }
                    }}

                  >
                    <span
                      style={
                        formData.room_category_id
                          ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                          : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                      }
                    >
                      {formData.room_category_id
                        ? formData.room_category_name || t("Category #{id}", { id: formData.room_category_id })
                        : t("Click to Select Category")}

                    </span>
                  </div>

                  {isEditingRoomCategory && (
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
                      style={{ zIndex: 1050 }}
                    >
                    <div className="rdp-pickerCard">
                      <div className="rdp-pickerHeader">
                        <div className="rdp-pickerHeaderCopy">
                          <p className="rdp-pickerEyebrow">{t("Accommodation")}</p>
                          <h5 className="rdp-pickerTitle">{t("Select Room Category")}</h5>
                          <p className="rdp-pickerSubtitle">
                            {t("Pick the category that best matches the reservation.")}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="rdp-pickerClose"
                          aria-label={t("Close")}
                          onClick={() => setIsEditingRoomCategory(false)}
                        >
                          &times;
                        </button>
                      </div>

                      {availableRoomCategories.length === 0 && (
                        <div className="rdp-pickerEmpty">{t("No categories available.")}</div>
                      )}

                      {availableRoomCategories.length > 0 && (
                        <div className="rdp-pickerOptions">
                          {availableRoomCategories
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((cat) => (
                              <div
                                key={cat.id}
                                className="rdp-pickerOption"
                                onClick={async () => {
                                  try {
                                    // 🔥 1️⃣ PATCH BD
                                    await axios.patch(
                                      `/api/reservation-drafts/${reservationDraftId}`,
                                      {
                                        room_category_id: cat.id,
                                        room_id: null,
                                        rate_id: null,
                                        group_rate_id: null,
                                        total_price: null,
                                        night_price: null,
                                        updated_by: "SelectRoomCategory",
                                      }
                                    );

                                    // 🔥 2️⃣ REBUILD FLAGS (draft-only, snapshot-aware)
                                    await axios.post(
                                      `/api/reservation-drafts/${reservationDraftId}/rebuild-flags`
                                    );

                                    // 🔁 3️⃣ RELEER DRAFT (BD manda)
                                    const { data: freshDraft } = await axios.get(
                                      `/api/reservation-drafts/${reservationDraftId}`
                                    );

                                    setFormData(freshDraft);
                                    setIsEditingRoomCategory(false);


                                    console.log("[RoomCategory][APPLY]", {
                                      reservation_draft_id: reservationDraftId,
                                      room_category_id: cat.id,
                                    });
                                  } catch (err) {
                                    if (err.response?.status === 409) {
                                      alert(
                                        t("Availability has changed. Please select dates again.")
                                      );

                                      // 🔥 Reset completo del draft (excepto fechas)
                                      await axios.patch(
                                        `/api/reservation-drafts/${reservationDraftId}`,
                                        {
                                          check_in_date: null,
                                          check_out_date: null,
                                          room_nights: null,
                                          adults: null,
                                          children: null,
                                          room_category_id: null,
                                          room_id: null,
                                          rate_id: null,
                                          total_price: null,
                                          night_price: null,
                                          updated_by: "ResetAfterNoAvailability"
                                        }
                                      );


                                      const { data: freshDraft } = await axios.get(
                                        `/api/reservation-drafts/${reservationDraftId}`
                                      );

                                      setFormData(freshDraft);
                                      setIsEditingRoomCategory(false);
                                      return;
                                    }

                                    console.error("[RoomCategory][ERROR_APPLY]", err);
                                    alert(t("Error saving room category."));
                                  }


                                }}
                              >
                                <span className="rdp-pickerOptionTitle">{cat.name}</span>
                              </div>
                            ))}
                        </div>
                      )}

                      <div className="rdp-pickerActions">
                        <button
                          type="button"
                          className="rdp-actionBtn rdp-actionBtn--secondary"
                          onClick={() => setIsEditingRoomCategory(false)}
                        >
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                </div>



                <div className="col-md-6 mb-3 position-relative">
                <label className="form-label">{t("Room Suggested")}</label>

                {formData.room_category_id ? (
                  <>
                    <div
                      className="form-control form-control-sm bg-light text-dark"
                      style={{
                        cursor: "pointer",
                        minHeight: "42px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={async () => {
                        if (roomSavingRef.current) return;
                        setRoomSelectionError("");
                        try {
                          const res = await axios.get(`/api/rooms/suggestion-options`, {
                            params: {
                              business_unit_id: formData.business_unit_id,
                              room_category_id: formData.room_category_id,
                              check_in_date: formData.check_in_date,
                              check_out_date: formData.check_out_date,
                              exclude_block_id: formData.source_block_id || undefined,
                              reservation_draft_group_id:
                                formData.reservation_group_id || undefined,
                              exclude_reservation_draft_id:
                                reservationDraftId || undefined,
                            },
                          });

                          setAvailableRooms(res?.data?.available || []);
                          setOccupiedRooms(res?.data?.occupied || []);
                          setIsEditingRoom(true);

                          console.log("[Room][LOAD_OPTIONS]", {
                            reservation_draft_id: reservationDraftId,
                            rooms: res.data,
                          });
                        } catch (err) {
                          console.error("[Room][ERROR_LOADING_OPTIONS]", err);
                          alert(t("Error fetching room suggestion options."));
                        }
                      }}
                    >
                      <span
                        style={
                          formData.room_id
                            ? { fontWeight: 600, fontSize: "0.95rem", lineHeight: "1.2" }
                            : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                        }
                      >
                        {formData.room_id
                          ? formData.room_name || t("Suggested room selected")
                          : t("Click to Select Suggested Room Number")}
                      </span>
                    </div>

                    {isEditingRoom && (
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
                      style={{ zIndex: 1050 }}
                    >
                      <div className="rdp-pickerCard rdp-pickerCard--wide">
                        <div className="rdp-pickerHeader">
                          <div className="rdp-pickerHeaderCopy">
                            <p className="rdp-pickerEyebrow">{t("Accommodation")}</p>
                            <h5 className="rdp-pickerTitle">{t("Select Suggested Room")}</h5>
                            <p className="rdp-pickerSubtitle">
                              {t("Review available and occupied rooms before applying the suggestion.")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rdp-pickerClose"
                            aria-label={t("Close")}
                            disabled={roomSaving}
                            onClick={() => setIsEditingRoom(false)}
                          >
                            &times;
                          </button>
                        </div>

                        <RoomUnassignOption
                          disabled={roomSaving}
                          busy={roomSaving}
                          onClick={() => applySuggestedRoom(null)}
                          t={t}
                        />

                        {roomSelectionError && (
                          <div className="alert alert-danger py-2" role="alert">{t(roomSelectionError)}</div>
                        )}

                        {availableRooms.length === 0 && occupiedRooms.length === 0 && (
                          <div className="rdp-pickerEmpty">{t("No rooms found.")}</div>
                        )}

                        {availableRooms.length > 0 && (
                          <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--available">{t("Available")}</div>
                        )}
                        {availableRooms.length > 0 && (
                          <div className="rdp-pickerOptions">
                            {sortRoomsByNumber(availableRooms).map((room) => (
                              <button
                                type="button"
                                key={`available-${room.id}`}
                                className="rdp-pickerOption"
                                disabled={roomSaving}
                                onClick={() => applySuggestedRoom(room)}
                              >
                                <span className="rdp-pickerOptionTitle">{t("Room")} {room.room_number}</span>
                                <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                                  {getRoomMetadataLines(room).map((line) => (
                                    <span key={line.label} className="rdp-pickerRoomMetaLine">
                                      <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                      {line.value}
                                    </span>
                                  ))}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        {occupiedRooms.length > 0 && (
                          <div className="rdp-pickerGroupLabel rdp-pickerGroupLabel--occupied">{t("Occupied / Blocked")}</div>
                        )}
                        {occupiedRooms.length > 0 && (
                          <div className="rdp-pickerOptions">
                            {sortRoomsByNumber(occupiedRooms).map((room) => (
                              <button
                                type="button"
                                key={`occupied-${room.id}`}
                                className="rdp-pickerOption rdp-pickerOption--occupied"
                                disabled={roomSaving}
                                onClick={() => applySuggestedRoom(room)}
                              >
                                <span className="rdp-pickerOptionTitle">{t("Room")} {room.room_number}</span>
                                <span className="rdp-pickerOptionMeta rdp-pickerRoomMeta">
                                  {getRoomMetadataLines(room).map((line) => (
                                    <span key={line.label} className="rdp-pickerRoomMetaLine">
                                      <span className="rdp-pickerRoomMetaLabel">{t(line.label)}:</span>{" "}
                                      {line.value}
                                    </span>
                                  ))}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="rdp-pickerActions">
                          <button
                            type="button"
                            className="rdp-actionBtn rdp-actionBtn--secondary"
                            disabled={roomSaving}
                            onClick={() => setIsEditingRoom(false)}
                          >
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  </>
                  ) : (
                    <div
                      className="form-control form-control-sm bg-light text-dark"
                      style={{ opacity: 0.6, minHeight: "42px", display: "flex", alignItems: "center" }}
                    >
                      {t("Select Room Category First")}
                    </div>
                  )}
                  </div>




                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Room Rate")}</label>

                  <div
                    className="mt-2 small text-muted border rounded p-2 bg-light text-dark d-flex justify-content-between align-items-center"
                    style={{
                      cursor:
                        ratePriceModificationBlock || !formData.room_category_id
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        ratePriceModificationBlock || !formData.room_category_id
                          ? 0.6
                          : 1,
                      minHeight: "42px"
                    }}
                    title={ratePriceModificationBlock?.message}
                    onClick={async () => {
                      if (showModificationBlock(ratePriceModificationBlock)) return;
                      if (formData.group_rate_id) {
                        alert(t("Reservations with Group Rate cannot be modified. Please delete and create a new reservation."));
                        return;
                      }

                      if (!formData.room_category_id) return;

                      if (!formData.check_in_date || !formData.check_out_date) {
                        alert(t("Select dates first."));
                        return;
                      }

                      if (formData.adults == null || formData.adults === "") {
                        alert(t("Select number of adults first."));
                        return;
                      }

                      try {
                        // 0️⃣ Si hay overrides → eliminarlos ANTES de abrir selector
                        if (formData.has_override) {
                          await axios.delete(
                            `/api/reservation-drafts/${reservationDraftId}/overrides`
                          );

                          const { data: refreshedDraft } = await axios.get(
                            `/api/reservation-drafts/${reservationDraftId}`
                          );

                          setFormData(refreshedDraft);
                        }

                        console.log("[Rates][DraftPopup][UI] fetching valid room rates", {
                          reservation_draft_id: reservationDraftId,
                          room_category_id: formData.room_category_id,
                          check_in_date: formData.check_in_date,
                          check_out_date: formData.check_out_date,
                          adults: formData.adults,
                          children: formData.children
                        });

                        setRoomRates([]);

                        const { data } = await axios.get(
                          `/api/reservation-drafts/${reservationDraftId}/valid-room-rates`
                        );

                        const rates = Array.isArray(data?.room_rates) ? data.room_rates : [];
                        setRoomRates(rates);

                        console.log("[Rates][DraftPopup][UI] rates loaded", {
                          reservation_draft_id: reservationDraftId,
                          total_candidates: data?.total_candidates,
                          total_valid: data?.total_valid,
                          rendered: rates.length,
                          reason_code: data?.reason_code,
                          reason: data?.reason || data?.reason_message || data?.message || data?.error
                        });

                        if (!rates.length) {
                          const msg = formatNoRatesReason(data, t, languageId);
                          console.warn("[Rates][DraftPopup][UI][NO_RATES]", { reservation_draft_id: reservationDraftId, msg, data });
                          alert(msg);
                          setIsEditingRate(false);
                          return;
                        }

                        setIsEditingRate(true);

                      } catch (err) {
                        console.error("[Rates][DraftPopup][UI][ERROR]", err);
                        setRoomRates([]);
                        alert(t("Error loading room rates."));
                      }
                    }}
                  >
                    <span
                      style={
                        formData.rate_id
                          ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                          : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                      }
                    >
                      {(() => {
                        if (!formData.rate_id) return t("Click to Select Room Rate");

                        // ✅ Fuente de verdad: viene del GET /api/reservation-drafts/:id
                        if (formData.rate_name) return formData.rate_name;

                        // fallback por si justo vienes de abrir selector y aún no refetch
                        for (let i = 0; i < roomRates.length; i++) {
                          const r = roomRates[i];
                          if (Number(r.id) === Number(formData.rate_id)) return r.name;
                        }

                        return t("Rate #{id}", { id: formData.rate_id });

                      })()}
                    </span>
                  </div>

                  {isEditingRate && (
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rdp-pickerOverlay"
                      style={{ zIndex: 1050 }}
                    >
                      <div className="rdp-pickerCard rdp-pickerCard--wide">
                        <div className="rdp-pickerHeader">
                          <div className="rdp-pickerHeaderCopy">
                            <p className="rdp-pickerEyebrow">{t("Pricing")}</p>
                            <h5 className="rdp-pickerTitle">{t("Select Room Rate")}</h5>
                            <p className="rdp-pickerSubtitle">
                              {t("Select the pricing rule to apply to the reservation.")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rdp-pickerClose"
                            aria-label={t("Close")}
                            onClick={() => setIsEditingRate(false)}
                          >
                            &times;
                          </button>
                        </div>

                        {roomRates.length === 0 && (
                          <div className="rdp-pickerEmpty">{t("No room rates available.")}</div>
                        )}

                        {roomRates.length > 0 && (
                          <div className="rdp-pickerOptions">
                            {roomRates.map((rate) => (
                              <div
                                key={rate.id}
                                className="rdp-pickerOption"
                                onClick={async () => {
                                  setIsEditingRate(false);

                                  const selectedRateId = rate.id;

                                  try {
                                    // 1️⃣ Persistir selección en BD (fuente de verdad)
                                    await axios.patch(
                                      `/api/reservation-drafts/${reservationDraftId}`,
                                      {
                                        rate_id: selectedRateId,
                                        group_rate_id: null,
                                        updated_by: "SelectRoomRate"
                                      }
                                    );

                                    // 2️⃣ Releer draft (BD manda)
                                    const { data: freshDraftAfterRate } = await axios.get(
                                      `/api/reservation-drafts/${reservationDraftId}`
                                    );
                                    setFormData(freshDraftAfterRate);

                                    // ⛔ NO recalcular flags ni rebuild snapshot aquí.
                                    // Al seleccionar rate:
                                    // - los flags de ESTA reserva deben quedarse congelados
                                    // - el consumo se aplica SOLO al final, preservando esta draft


                                    // 3️⃣ Generar nightly prices DRAFT (BD)
                                    await axios.post(
                                      `/api/reservation-drafts/${reservationDraftId}/save-nightly-prices`,
                                      {
                                        room_rate_id: freshDraftAfterRate.rate_id,
                                        check_in_date: freshDraftAfterRate.check_in_date,
                                        check_out_date: freshDraftAfterRate.check_out_date
                                      }
                                    );

                                    // 4️⃣ Calcular totales desde nightly prices DRAFT (BD)
                                    await axios.post(
                                      `/api/reservation-drafts/${reservationDraftId}/totals/sync-from-draft-nightly-prices`
                                    );

                                    // ✅ 4.5️⃣ Consumir snapshot del grupo (preserva flags de ESTA reserva)
                                    await axios.post(`/api/reservation-drafts/draft-availability/apply-consumption`, {
                                      reservation_group_id: freshDraftAfterRate.reservation_group_id,
                                      business_unit_id: freshDraftAfterRate.business_unit_id,
                                      reservation_draft_id: reservationDraftId,
                                    });

                                    // 5️⃣ Refetch final (SOLO BD)
                                    const { data: freshFinal } = await axios.get(
                                      `/api/reservation-drafts/${reservationDraftId}`
                                    );
                                    setFormData(freshFinal);
                                    await onPricingChanged?.();

                                    console.log("[Rate][Apply][DB_OK]", {
                                      reservation_draft_id: reservationDraftId,
                                      rate_id: selectedRateId
                                    });
                                  } catch (err) {
                                    if (err.response?.status === 409) {
                                      alert(
                                        t("Availability has changed. Please select dates again.")
                                      );

                                      await axios.patch(
                                        `/api/reservation-drafts/${reservationDraftId}`,
                                        {
                                          check_in_date: null,
                                          check_out_date: null,
                                          room_nights: null,
                                          adults: null,
                                          children: null,
                                          room_category_id: null,
                                          room_id: null,
                                          rate_id: null,
                                          total_price: null,
                                          night_price: null,
                                          updated_by: "ResetAfterNoAvailabilityRate"
                                        }
                                      );

                                      const { data: freshDraft } = await axios.get(
                                        `/api/reservation-drafts/${reservationDraftId}`
                                      );

                                      setFormData(freshDraft);
                                      setIsEditingRate(false);
                                      return;
                                    }

                                    console.error("[Rate][ERROR_APPLY]", err);
                                    alert(t("Error saving rate."));
                                  }

                                }}
                              >
                                <span className="rdp-pickerOptionTitle">{rate.name}</span>
                                {rate.client?.name && (
                                  <span className="rdp-pickerOptionMeta">{rate.client.name}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="rdp-pickerActions">
                          <button
                            type="button"
                            className="rdp-actionBtn rdp-actionBtn--secondary"
                            onClick={() => setIsEditingRate(false)}
                          >
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-md-3 mb-3 d-flex align-items-end">
                  {(() => {
                    const hasOverride = Boolean(formData?.has_override);

                    const titleText = hasOverride
                      ? (formData?.rate_id
                          ? t("Remove overrides and restore base nightly prices")
                          : t("Select a Room Rate first"))
                      : (formData?.rate_id
                          ? t("Apply overrides to nightly prices")
                          : t("Select a Room Rate first"));

                    const labelText = hasOverride ? t("Remove Override") : t("Apply Override");
                    const outlineClass = hasOverride ? "btn-outline-danger" : "btn-outline-success";

                    return (
                      <button
                        type="button"
                        className={`btn ${outlineClass} w-100 fw-bold d-flex align-items-center justify-content-center mt-2`}
                        style={{ height: "42px" }}
                        disabled={
                          Boolean(ratePriceModificationBlock) ||
                          !formData?.rate_id
                        }
                        title={ratePriceModificationBlock?.message || titleText}
                        onClick={async () => {
                          if (showModificationBlock(ratePriceModificationBlock)) {
                            return;
                          }
                          if (!formData?.rate_id) return;

                          if (hasOverride) {
                            try {
                              await axios.delete(`/api/reservation-drafts/${reservationDraftId}/overrides`);
                              await refetchDraftFromDB();
                            } catch (err) {
                              console.error("[Overrides][UI] Remove failed:", err);
                              alert(t("Error removing overrides."));
                            }
                          } else {
                            setIsOverridePopupOpen(true);
                          }
                        }}
                      >
                        {labelText}
                      </button>
                    );
                  })()}
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label">{t("Room Price (")}{displayCurrency})</label>
                  <div className="mt-2 border rounded p-2 bg-light text-dark rdp-valueField" style={{ minHeight: "42px", display: "flex", alignItems: "center" }}>
                    <span
                      style={
                        formData.total_price === 0
                          ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                          : formData.total_price != null
                          ? { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                          : { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                      }
                    >
                      {formData.total_price !== null && formData.total_price !== undefined
                        ? formatFromBase(formData.total_price)
                        : t("Pending")}
                    </span>
                  </div>
                </div>

                {/* Extras details */}
                <div className="col-md-5">
                  <label className="form-label">{t("Extras details")}</label>
                  <input
                    type="text"
                    readOnly
                    value={getExtrasNames(formData, t)}

                    className="form-control bg-secondary text-light fw-bold mt-2 rdp-readonlyField rdp-extrasField"
                    style={{ height: "42px", fontSize: "0.95rem", lineHeight: "1.2" }}
                    onClick={() => setExtrasModalMode("details")}
                  />
                </div>

                {/* Manage extras */}
                <div className="col-md-2">
                  <label className="form-label">&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100 fw-bold d-flex align-items-center justify-content-center mt-2"
                    style={{ height: "42px" }}
                    onClick={() => setExtrasModalMode("manage")}
                    disabled={!formData.rate_id || !formData.total_price}
                    title={
                      !formData.rate_id || !formData.total_price
                        ? t("Select a Room Rate first to manage extras")
                        : t("Manage Extras")
                    }
                  >
                    {t("Manage Extras")}
                  </button>
                </div>

                {/* Total Extras */}
                <div className="col-md-2">
                  <label className="form-label">{t("Total Extras (")}{displayCurrency})</label>
                  <div
                    className="border rounded p-2 bg-light text-dark d-flex align-items-center mt-2 rdp-valueField"
                    style={{ height: "42px" }}
                    title={t("Total applied extras")}
                  >
                    <span
                      style={
                        Number(formData.total_extras || 0) === 0
                          ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                          : { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                      }
                    >
                      {formatFromBase(getTotalExtras(formData))}

                    </span>
                  </div>
                </div>

                {/* Final Total Price */}
                <div className="col-md-3">
                  <label className="form-label">{t("Final Total Price (")}{displayCurrency})</label>
                  <div
                    className="border rounded p-2 bg-light text-dark d-flex align-items-center mt-2 rdp-valueField rdp-valueField--total"
                    style={{ height: "42px" }}
                    title={t("Reservation total including extras")}
                  >
                    {formData.total_price != null && (
                      <span
                        style={
                          Number(formData.total_price) + Number(formData.total_extras || 0) === 0
                            ? { color: "#6c757d", fontSize: "0.875rem", lineHeight: "1.2" }
                            : { fontWeight: "600", fontSize: "0.95rem", lineHeight: "1.2" }
                        }
                      >
                        {formatFromBase(Number(formData.total_price || 0) + getTotalExtras(formData))}

                      </span>
                    )}
                  </div>
                </div>

              </div>
              </section>
              <section className="rdp-section rdp-section--notes" style={sectionStyle}>
                <div className="rdp-sectionTitle">{t("Extras and Notes")}</div>
                <div className="row g-3">

                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Check-in Time")}</label>
                  <input
                    key={`${reservationDraftId}-in-${formData.check_in_time ?? "null"}`}
                    type="time"
                    name="check_in_time"
                    defaultValue={toHHMM(formData.check_in_time)}
                    onBlur={async (e) => {
                      const check_in_time = toDbTime(e.target.value);

                      await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                        check_in_time,
                        updated_by: "SelectCheckInTime"
                      });

                      const { data: freshDraft } = await axios.get(
                        `/api/reservation-drafts/${reservationDraftId}`
                      );

                      setFormData(freshDraft);
                    }}
                    className="form-control form-control-sm p-1"
                  />
                </div>

                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Check-out Time")}</label>
                  <input
                    key={`${reservationDraftId}-out-${formData.check_out_time ?? "null"}`}
                    type="time"
                    name="check_out_time"
                    defaultValue={toHHMM(formData.check_out_time)}
                    onBlur={async (e) => {
                      const check_out_time = toDbTime(e.target.value);

                      await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                        check_out_time,
                        updated_by: "SelectCheckOutTime"
                      });

                      const { data: freshDraft } = await axios.get(
                        `/api/reservation-drafts/${reservationDraftId}`
                      );

                      setFormData(freshDraft);
                    }}
                    className="form-control form-control-sm p-1"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("Special Requests")}</label>
                  <textarea
                    key={`${reservationDraftId}-${formData.special_requests ?? "null"}`}
                    name="special_requests"
                    defaultValue={formData.special_requests ?? ""}
                    onBlur={async (e) => {
                      const special_requests = e.target.value.trim();

                      await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                        special_requests,
                        updated_by: "AutoSaveSpecialRequests"
                      });

                      const { data: freshDraft } = await axios.get(
                        `/api/reservation-drafts/${reservationDraftId}`
                      );

                      setFormData(freshDraft);
                    }}
                    className="form-control form-control-sm p-1"
                    rows="1"
                    style={{ resize: "none" }}
                  />
                </div>

                <div className="col-md-2 mb-3">
                  <label className="form-label">{t("Reservation Status")}</label>
                  <input
                    readOnly
                    className="form-control bg-secondary text-light fw-bold text-center rdp-statusField"
                    value={translateReservationStatus(formData.status, languageId)}
                  />
                </div>
              </div>
              </section>

              {/* FOOTER */}
              <div className="d-flex gap-2 mt-3 rdp-formActions">
                <button
                  type="button"
                  className="btn btn-success fw-bold flex-fill"
                  style={{ minHeight: "48px" }}
                  disabled={roomSaving}
                  onClick={async () => {
                    // fuerza refetch final por si el último onBlur aún no se reflejó
                    await refetchDraftFromDB();
                    if (typeof onSaved === "function") await onSaved();
                    onClose();
                  }}
                >
                  {t("Save Reservation")}
                </button>


                <button
                  type="button"
                  className="btn btn-outline-danger fw-bold flex-fill"
                  style={{ minHeight: "48px" }}
                  disabled={roomSaving}
                  onClick={onClose}
                >
                  {t("Close")}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {isEditingDates && (
        <div className="rdp-pickerOverlay rdp-pickerOverlay--calendar" style={calendarOverlayStyle}>
          <div className="rdp-pickerCard rdp-pickerCard--wide rdp-dateModal" style={calendarPanelStyle}>
            <div className="rdp-pickerHeader">
              <div className="rdp-pickerHeaderCopy">
                <p className="rdp-pickerEyebrow">{t("Dates")}</p>
                <h5 className="rdp-pickerTitle">{t("Edit Stay Dates")}</h5>
                <p className="rdp-pickerSubtitle">
                  {t("Update the check-in and check-out dates without changing any reservation logic.")}
                </p>
              </div>

              <button
                type="button"
                className="rdp-pickerClose"
                aria-label={t("Close date editor")}
                onClick={() => setIsEditingDates(false)}
              >
                &times;
              </button>
            </div>

            <div className="rdp-dateContent">
              <div className="rdp-dateFields">
                <div className="rdp-dateFieldGroup">
                  <label className="rdp-dateFieldLabel" htmlFor="rdp-check-in-date">
                    {t("Check-in")}
                  </label>
                  <DatePicker
                    selected={tempRange.startDate}
                    onChange={(date) => setTempRange({ ...tempRange, startDate: date })}
                    selectsStart
                    startDate={tempRange.startDate}
                    endDate={tempRange.endDate}
                    minDate={new Date()}
                    maxDate={tempRange.endDate}
                    locale={datePickerLocale}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t("Select check-in")}
                    customInput={
                      <input
                        id="rdp-check-in-date"
                        className="rdp-dateField"
                        aria-label={t("Selected check-in date")}
                        readOnly
                      />
                    }
                    open={false}
                  />
                </div>

                <div className="rdp-dateFieldGroup">
                  <label className="rdp-dateFieldLabel" htmlFor="rdp-check-out-date">
                    {t("Check-out")}
                  </label>
                  <DatePicker
                    selected={tempRange.endDate}
                    onChange={(date) => setTempRange({ ...tempRange, endDate: date })}
                    selectsEnd
                    startDate={tempRange.startDate}
                    endDate={tempRange.endDate}
                    minDate={tempRange.startDate || new Date()}
                    locale={datePickerLocale}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t("Select check-out")}
                    customInput={
                      <input
                        id="rdp-check-out-date"
                        className="rdp-dateField"
                        aria-label={t("Selected check-out date")}
                        readOnly
                      />
                    }
                    open={false}
                  />
                </div>
              </div>

              <div className="rdp-dateCalendarIntro">
                <span>
                  <strong>{t("Select the stay:")}</strong> {t("choose the check-in date first, then the check-out date.")}
                </span>
                {selectedDateNights ? (
                  <span className="rdp-dateCalendarSummary">
                    {selectedDateNights} {selectedDateNights === 1 ? t("night") : t("nights")}
                  </span>
                ) : null}
              </div>

              <div className="rdp-dateCalendarWrap">
                <DatePicker
                  selected={tempRange.startDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setTempRange({ startDate: start, endDate: end });
                  }}
                  startDate={tempRange.startDate}
                  endDate={tempRange.endDate}
                  selectsRange
                  minDate={new Date()}
                  monthsShown={2}
                  locale={datePickerLocale}
                  previousMonthAriaLabel={t("Previous month")}
                  nextMonthAriaLabel={t("Next month")}
                  previousMonthButtonLabel={t("Previous month")}
                  nextMonthButtonLabel={t("Next month")}
                  chooseDayAriaLabelPrefix={t("Choose")}
                  disabledDayAriaLabelPrefix={t("Not available")}
                  monthAriaLabelPrefix={t("Month")}
                  weekAriaLabelPrefix={t("Week")}
                  dateFormat="dd/MM/yyyy"
                  calendarClassName="rdp-dateCalendar"
                  dayClassName={() => "rdp-dateCalendarDay"}
                  inline
                />
              </div>
            </div>

            <div className="rdp-pickerActions">
              <button
                type="button"
                className="rdp-actionBtn rdp-actionBtn--secondary"
                onClick={() => {
                  setIsEditingDates(false);
                  setTempRange({ startDate: null, endDate: null });
                }}
              >
                {t("Cancel")}
              </button>

              <button
                type="button"
                className="rdp-actionBtn rdp-actionBtn--primary"
                disabled={!hasCompleteDateRange}
                onClick={async () => {
                  const { startDate, endDate } = tempRange;

                  if (!startDate || !endDate) {
                    alert(t("Please select both dates."));
                    return;
                  }

                  if (endDate <= startDate) {
                    alert(t("Check-out must be after check-in."));
                    return;
                  }

                  const checkInStr = toLocalYYYYMMDD(startDate);
                  const checkOutStr = toLocalYYYYMMDD(endDate);
                  const nights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

                  try {
                    await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                      check_in_date: checkInStr,
                      check_out_date: checkOutStr,
                      room_nights: nights,
                      updated_by: "PopupDates",
                    });

                    const populateRes = await axios.post(
                      `/api/reservation-drafts/draft-availability/populate`,
                      {
                        reservation_draft_id: reservationDraftId,
                        reservation_group_id: formData.reservation_group_id,
                        business_unit_id: formData.business_unit_id,
                        start_date: checkInStr,
                        end_date: checkOutStr,
                      }
                    );

                    if (populateRes.data?.has_availability !== true) {
                      alert(t("No availability for the selected dates."));

                      await axios.patch(`/api/reservation-drafts/${reservationDraftId}`, {
                        check_in_date: null,
                        check_out_date: null,
                        room_nights: null,
                        updated_by: "DatesNoAvailabilityRollback",
                      });

                      const { data: rolledBack } = await axios.get(
                        `/api/reservation-drafts/${reservationDraftId}`
                      );

                      setFormData(rolledBack);
                      setIsEditingDates(false);
                      setTempRange({ startDate: null, endDate: null });
                      return;
                    }

                    const { data: freshDraft } = await axios.get(
                      `/api/reservation-drafts/${reservationDraftId}`
                    );

                    setFormData(freshDraft);
                    setTempRange({ startDate: null, endDate: null });
                    setIsEditingDates(false);
                  } catch (err) {
                    console.error("❌ Error saving dates:", err);
                    alert(t("Error saving dates."));
                  }
                }}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}

       <OverridePopup
        reservationId={reservationDraftId}
        baseCurrency={baseCurrency}
        isOpen={isOverridePopupOpen}
        onClose={() => setIsOverridePopupOpen(false)}
        onApplied={async () => {
          await refetchDraftFromDB();
        }}
      />

      <ReservationExtrasModal
        show={extrasModalMode === "manage" || extrasModalMode === "details"}
        onClose={() => setExtrasModalMode(null)}
        reservationId={reservationDraftId}
        businessUnitId={formData.business_unit_id}
        onSaved={async () => {
          const { data: freshDraft } = await axios.get(
            `/api/reservation-drafts/${reservationDraftId}`
          );
          setFormData(freshDraft);
        }}
      />




    </>
  );
};

export default ReservationDraftPopup;
